import { api } from '@/lib/api';
import type {
  CostAggregate,
  CostGroupBy,
  WorkOrderCost,
} from './types';

/**
 * L8 — cost / fleet budget REST client.
 *
 * Endpoints (backend module `wo-cost`):
 *   GET  /work-orders/:id/cost
 *   GET  /work-orders/:id/cost.xlsx     → returns a Blob for download
 *   GET  /vessels/:vesselId/cost?from=&to=&groupBy=month|category
 *   GET  /companies/:companyId/cost?from=&to=&groupBy=vessel|month|woType
 */
export const costService = {
  async getWorkOrderCost(workOrderId: string): Promise<WorkOrderCost> {
    const { data } = await api.get<WorkOrderCost>(
      `/work-orders/${workOrderId}/cost`,
    );
    return data;
  },

  async getVesselCost(input: {
    vesselId: string;
    from?: Date;
    to?: Date;
    groupBy?: Extract<CostGroupBy, 'month' | 'category'>;
  }): Promise<CostAggregate> {
    const params: Record<string, string> = {};
    if (input.from) params.from = input.from.toISOString();
    if (input.to) params.to = input.to.toISOString();
    if (input.groupBy) params.groupBy = input.groupBy;
    const { data } = await api.get<CostAggregate>(
      `/vessels/${input.vesselId}/cost`,
      { params },
    );
    return data;
  },

  async getCompanyCost(input: {
    companyId: string;
    from?: Date;
    to?: Date;
    groupBy?: Extract<CostGroupBy, 'vessel' | 'month' | 'woType'>;
  }): Promise<CostAggregate> {
    const params: Record<string, string> = {};
    if (input.from) params.from = input.from.toISOString();
    if (input.to) params.to = input.to.toISOString();
    if (input.groupBy) params.groupBy = input.groupBy;
    const { data } = await api.get<CostAggregate>(
      `/companies/${input.companyId}/cost`,
      { params },
    );
    return data;
  },

  /**
   * Returns the raw XLSX bytes for a Work Order. The caller is
   * responsible for triggering the browser download (see
   * `triggerDownload`).
   */
  async downloadWorkOrderXlsx(workOrderId: string): Promise<Blob> {
    const res = await api.get(`/work-orders/${workOrderId}/cost.xlsx`, {
      responseType: 'blob',
    });
    return res.data as Blob;
  },
};

/**
 * Triggers a save-as dialog for the given Blob. We do this via a
 * synthetic `<a download>` because the Web Streams `showSaveFilePicker`
 * is not available on Safari, and the legacy `window.open(URL)` route
 * leaks object URLs.
 */
export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Schedule revocation on the next tick so the browser has time to
    // start the download (immediate revocation cancels it on Firefox).
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
